import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Spinner } from '../../components/ui/spinner';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, User, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerCreatePage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    referred_by_customer_id: ''
  });

  const [phoneError, setPhoneError] = useState('');
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [referralSearch, setReferralSearch] = useState('');
  const [showReferralSuggestions, setShowReferralSuggestions] = useState(false);
  const [referralEnabled, setReferralEnabled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/customers?limit=500`).then(r => setExistingCustomers(r.data || [])).catch(() => {});
    axios.get(`${API}/marketing/loyalty/settings`).then(r => setReferralEnabled(!!r.data?.referral_enabled)).catch(() => {});
  }, []);

  const validatePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 11) {
      return language === 'en' ? 'Phone number must be 11 digits' : '\u09AB\u09CB\u09A8 \u09A8\u09AE\u09CD\u09AC\u09B0 \u0985\u09AC\u09B6\u09CD\u09AF\u0987 \u09E7\u09E7 \u09A1\u09BF\u099C\u09BF\u099F \u09B9\u09A4\u09C7 \u09B9\u09AC\u09C7';
    }
    if (!/^01/.test(digits)) {
      return language === 'en' ? 'Phone number must start with 01' : '\u09AB\u09CB\u09A8 \u09A8\u09AE\u09CD\u09AC\u09B0 01 \u09A6\u09BF\u09AF\u09BC\u09C7 \u09B6\u09C1\u09B0\u09C1 \u09B9\u09A4\u09C7 \u09B9\u09AC\u09C7';
    }
    return '';
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'phone') {
      setPhoneError(value ? validatePhone(value) : '');
    }
  };

  const selectedReferrer = existingCustomers.find(c => c.id === formData.referred_by_customer_id);
  const filteredReferrers = existingCustomers
    .filter(c => c.id !== formData.referred_by_customer_id)
    .filter(c => c.name?.toLowerCase().includes(referralSearch.toLowerCase()) || c.phone?.includes(referralSearch))
    .slice(0, 10);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast({ title: language === 'en' ? 'Name and phone are required' : 'নাম এবং ফোন প্রয়োজন', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.referred_by_customer_id) delete payload.referred_by_customer_id;
      const response = await axios.post(`${API}/customers`, payload);
      toast({ title: language === 'en' ? 'Customer created!' : 'গ্রাহক তৈরি হয়েছে!' });
      navigate(`/admin/customers/${response.data.id}`);
    } catch (error) {
      toast({ 
        title: language === 'en' ? 'Failed to create customer' : 'গ্রাহক তৈরি ব্যর্থ',
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/customers')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{t('new_customer')}</h1>
            <p className="text-slate-500">
              {language === 'en' ? 'Add a new customer to your database' : 'আপনার ডাটাবেসে নতুন গ্রাহক যোগ করুন'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('customer_details')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">{language === 'en' ? 'Full Name' : 'পুরো নাম'} *</Label>
                <Input
                  id="name"
                  placeholder={language === 'en' ? 'Enter customer name' : 'গ্রাহকের নাম লিখুন'}
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="h-12"
                  data-testid="customer-name"
                />
              </div>

              <div>
                <Label htmlFor="phone">{t('phone')} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="01XXXXXXXXX"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  required
                  maxLength={11}
                  className={`h-12 ${phoneError ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  data-testid="customer-phone"
                />
                {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
              </div>

              <div>
                <Label htmlFor="email">{t('email')} ({language === 'en' ? 'optional' : 'ঐচ্ছিক'})</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="customer@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="h-12"
                  data-testid="customer-email"
                />
              </div>

              <div>
                <Label htmlFor="address">{t('address')}</Label>
                <Textarea
                  id="address"
                  placeholder={language === 'en' ? 'Enter address' : 'ঠিকানা লিখুন'}
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  data-testid="customer-address"
                />
              </div>

              <div>
                <Label htmlFor="notes">{t('notes')}</Label>
                <Textarea
                  id="notes"
                  placeholder={language === 'en' ? 'Any additional notes...' : 'অতিরিক্ত নোট...'}
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  data-testid="customer-notes"
                />
              </div>

              {/* Referred by */}
              {referralEnabled && (
              <div>
                <Label className="flex items-center gap-1">
                  <UserPlus className="w-4 h-4" />
                  {t('marketing_referred_by')} ({language === 'en' ? 'optional' : 'ঐচ্ছিক'})
                </Label>
                <Input
                  placeholder={language === 'en' ? 'Search existing customer...' : 'বিদ্যমান গ্রাহক খুঁজুন...'}
                  value={referralSearch}
                  onFocus={() => setShowReferralSuggestions(true)}
                  onChange={(e) => {
                    setReferralSearch(e.target.value);
                    if (formData.referred_by_customer_id) {
                      handleChange('referred_by_customer_id', '');
                    }
                    setShowReferralSuggestions(true);
                  }}
                  className="h-10 mb-1"
                />
                {selectedReferrer && (
                  <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-1 mb-1 inline-block">
                    {language === 'en' ? 'Selected referrer:' : 'নির্বাচিত রেফারার:'} {selectedReferrer.name} - {selectedReferrer.phone}
                  </div>
                )}
                {showReferralSuggestions && referralSearch.length >= 2 && (
                  <div className="border rounded-md max-h-40 overflow-y-auto">
                    {filteredReferrers.length > 0 ? filteredReferrers.map(c => (
                        <button key={c.id} type="button"
                          className="w-full text-left px-3 py-2 hover:bg-slate-100 text-sm"
                          onClick={() => {
                            handleChange('referred_by_customer_id', c.id);
                            setReferralSearch(`${c.name} - ${c.phone}`);
                            setShowReferralSuggestions(false);
                          }}>
                          {c.name} — {c.phone}
                        </button>
                      )) : <p className="px-3 py-2 text-xs text-slate-500">{language === 'en' ? 'No matching customer found' : 'মিলে এমন কোন গ্রাহক নেই'}</p>}
                  </div>
                )}
                {formData.referred_by_customer_id && (
                  <button type="button" className="text-xs text-red-500 mt-1" onClick={() => { handleChange('referred_by_customer_id', ''); setReferralSearch(''); setShowReferralSuggestions(false); }}>
                    {language === 'en' ? 'Clear referral' : 'রেফারেল মুছুন'}
                  </button>
                )}
              </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 bg-blue-800 hover:bg-blue-900 font-bold"
                disabled={loading}
                data-testid="submit-customer"
              >
                {loading ? <Spinner size="sm" /> : (language === 'en' ? 'Create Customer' : 'গ্রাহক তৈরি করুন')}
              </Button>
            </CardContent>
          </Card>
        </form>
      </div>
    </AdminLayout>
  );
}
